/**
 * Helpers para guardar snapshots de documentos en Firestore sin romper
 * por nombres de campo reservados.
 *
 * Firestore RECHAZA escribir docs cuyos field names comienzan o terminan
 * con doble underscore (`__`). Eso aplica también a campos anidados.
 *
 * Caso concreto que motivó esto: el módulo IA agrega un campo
 * `aiEmbedding` a los archivos con estructura tipo:
 *   { aiEmbedding: { __type__: '__vector__', value: [...] } }
 *
 * Cuando el flujo de DELETE de un archivo intenta guardar `parentData`
 * en `trash/{id}.restorePayload` o en `audit/{id}/before/{x}`, el
 * `set()` lanza:
 *   FirebaseError: Document fields cannot begin and end with "__"
 *     (found in field restorePayload.parentData.aiEmbedding.__type__)
 *
 * Solución: `sanitizeForFirestore(obj)` recorre recursivamente el objeto
 * y elimina cualquier campo cuya key empieza o termina con `__`.
 * No toca tipos especiales (Timestamp, DocumentReference, GeoPoint) ni
 * arrays — solo plain objects.
 */

const isPlainObject = (v) => {
  if (v === null || typeof v !== 'object') return false;
  if (Array.isArray(v)) return false;
  // Tipos especiales de Firestore SDK no son plain objects — preservarlos.
  const proto = Object.getPrototypeOf(v);
  return proto === Object.prototype || proto === null;
};

const hasReservedKey = (key) => {
  if (typeof key !== 'string') return false;
  return key.startsWith('__') || key.endsWith('__');
};

/**
 * Devuelve una copia profunda del objeto sin claves reservadas por Firestore.
 *
 * - Plain objects: elimina claves `__...` y recursa.
 * - Arrays: recursa elemento por elemento.
 * - Tipos especiales (Timestamp, DocumentReference, GeoPoint, FieldValue):
 *   se devuelven tal cual (no se mutan).
 * - Primitivos: pasan directamente.
 */
export const sanitizeForFirestore = (value) => {
  if (Array.isArray(value)) {
    return value.map(sanitizeForFirestore);
  }
  if (!isPlainObject(value)) {
    return value;
  }
  const out = {};
  for (const [k, v] of Object.entries(value)) {
    if (hasReservedKey(k)) continue;
    out[k] = sanitizeForFirestore(v);
  }
  return out;
};

export default sanitizeForFirestore;
